import * as React from "react";

import { activationClient, activationRouter } from "@v2/trpc/routes/activation";
import { affiliatesClient, affiliatesRouter } from "@v2/trpc/routes/affiliates";
import {
  agentIntegrationsClient,
  agentIntegrationsRouter,
} from "@v2/trpc/routes/agentIntegrations";
import { analyticsClient, analyticsRouter } from "@v2/trpc/routes/analytics";
import {
  analyticsLinkClient,
  analyticsLinkRouter,
} from "@v2/trpc/routes/analyticsLink";
import {
  announcementClient,
  announcementRouter,
} from "@v2/trpc/routes/announcement";
import { assetClient, assetRouter } from "@v2/trpc/routes/asset";
import { authClient, authRouter } from "@v2/trpc/routes/auth";
import { billingClient, billingRouter } from "@v2/trpc/routes/billing";
import { chatClient, chatRouter } from "@v2/trpc/routes/chat";
import { daytonaClient, daytonaRouter } from "@v2/trpc/routes/daytona";
import { elementClient, elementRouter } from "@v2/trpc/routes/element";
import { entriClient, entriRouter } from "@v2/trpc/routes/entri";
import { experimentClient, experimentRouter } from "@v2/trpc/routes/experiment";
import {
  fileTemplateClient,
  fileTemplateRouter,
} from "@v2/trpc/routes/fileTemplate";
import {
  fileVersionClient,
  fileVersionRouter,
} from "@v2/trpc/routes/fileVersion";
import { fontClient, fontRouter } from "@v2/trpc/routes/font";
import { funnelClient, funnelRouter } from "@v2/trpc/routes/funnel";
import {
  integrationClient,
  integrationRouter,
} from "@v2/trpc/routes/integration";
import { marketingClient, marketingRouter } from "@v2/trpc/routes/marketing";
import { modifiersClient, modifiersRouter } from "@v2/trpc/routes/modifiers";
import {
  nextJSProjectClient,
  nextJSProjectRouter,
} from "@v2/trpc/routes/nextJSProject";
import {
  nextJSProjectCacheClient,
  nextJSProjectCacheRouter,
} from "@v2/trpc/routes/nextJSProjectCache";
import { onboardingClient, onboardingRouter } from "@v2/trpc/routes/onboarding";
import { opencodeClient, opencodeRouter } from "@v2/trpc/routes/opencode";
import { productClient, productRouter } from "@v2/trpc/routes/product";
import {
  productFileClient,
  productFileRouter,
} from "@v2/trpc/routes/productFile";
import { projectClient, projectRouter } from "@v2/trpc/routes/project";
import {
  shopMetadataClient,
  shopMetadataRouter,
} from "@v2/trpc/routes/shopMetadata";
import {
  starterProjectsClient,
  starterProjectsRouter,
} from "@v2/trpc/routes/starterProjects";
import {
  stripeConnectClient,
  stripeConnectRouter,
} from "@v2/trpc/routes/stripeConnect";
import { stylesClient, stylesRouter } from "@v2/trpc/routes/styles";
import {
  subscriptionClient,
  subscriptionRouter,
} from "@v2/trpc/routes/subscription";
import { triggerClient, triggerRouter } from "@v2/trpc/routes/trigger";
import { userClient, userRouter } from "@v2/trpc/routes/user";
import {
  v2CustomDomainClient,
  v2CustomDomainRouter,
} from "@v2/trpc/routes/v2CustomDomain";
import {
  v2ShopDeploymentClient,
  v2ShopDeploymentRouter,
} from "@v2/trpc/routes/v2ShopDeployment";
import { workspaceClient, workspaceRouter } from "@v2/trpc/routes/workspace";
import { getNamespacedQueryClient } from "@v2/trpc/trpc";

/**
 * Wraps children with all TRPC-related providers required for V2.
 */
export function V2TRPCProviders({ children }: React.PropsWithChildren) {
  return (
    <analyticsRouter.Provider
      client={analyticsClient}
      queryClient={getNamespacedQueryClient("analytics")}
    >
      <analyticsLinkRouter.Provider
        client={analyticsLinkClient}
        queryClient={getNamespacedQueryClient("analyticsLink")}
      >
        {(affiliatesRouter as any).Provider({
          client: affiliatesClient,
          queryClient: getNamespacedQueryClient("affiliates"),
          children: (
            <announcementRouter.Provider
              client={announcementClient}
              queryClient={getNamespacedQueryClient("announcement")}
            >
              <assetRouter.Provider
                client={assetClient}
                queryClient={getNamespacedQueryClient("asset")}
              >
                <authRouter.Provider
                  client={authClient}
                  queryClient={getNamespacedQueryClient("auth")}
                >
                  <billingRouter.Provider
                    client={billingClient}
                    queryClient={getNamespacedQueryClient("billing")}
                  >
                    <chatRouter.Provider
                      client={chatClient}
                      queryClient={getNamespacedQueryClient("chat")}
                    >
                      <daytonaRouter.Provider
                        client={daytonaClient}
                        queryClient={getNamespacedQueryClient("daytona")}
                      >
                        <elementRouter.Provider
                          client={elementClient}
                          queryClient={getNamespacedQueryClient("element")}
                        >
                          <entriRouter.Provider
                            client={entriClient}
                            queryClient={getNamespacedQueryClient("entri")}
                          >
                            <experimentRouter.Provider
                              client={experimentClient}
                              queryClient={getNamespacedQueryClient(
                                "experiment",
                              )}
                            >
                              <fileTemplateRouter.Provider
                                client={fileTemplateClient}
                                queryClient={getNamespacedQueryClient(
                                  "fileTemplate",
                                )}
                              >
                                <fileVersionRouter.Provider
                                  client={fileVersionClient}
                                  queryClient={getNamespacedQueryClient(
                                    "fileVersion",
                                  )}
                                >
                                  <fontRouter.Provider
                                    client={fontClient}
                                    queryClient={getNamespacedQueryClient(
                                      "font",
                                    )}
                                  >
                                    <funnelRouter.Provider
                                      client={funnelClient}
                                      queryClient={getNamespacedQueryClient(
                                        "funnel",
                                      )}
                                    >
                                      <integrationRouter.Provider
                                        client={integrationClient}
                                        queryClient={getNamespacedQueryClient(
                                          "integration",
                                        )}
                                      >
                                        <marketingRouter.Provider
                                          client={marketingClient}
                                          queryClient={getNamespacedQueryClient(
                                            "marketing",
                                          )}
                                        >
                                          <modifiersRouter.Provider
                                            client={modifiersClient}
                                            queryClient={getNamespacedQueryClient(
                                              "modifiers",
                                            )}
                                          >
                                            <nextJSProjectRouter.Provider
                                              client={nextJSProjectClient}
                                              queryClient={getNamespacedQueryClient(
                                                "nextJSProject",
                                              )}
                                            >
                                              <nextJSProjectCacheRouter.Provider
                                                client={
                                                  nextJSProjectCacheClient
                                                }
                                                queryClient={getNamespacedQueryClient(
                                                  "nextJSProjectCache",
                                                )}
                                              >
                                                <onboardingRouter.Provider
                                                  client={onboardingClient}
                                                  queryClient={getNamespacedQueryClient(
                                                    "onboarding",
                                                  )}
                                                >
                                                  <productRouter.Provider
                                                    client={productClient}
                                                    queryClient={getNamespacedQueryClient(
                                                      "product",
                                                    )}
                                                  >
                                                    <productFileRouter.Provider
                                                      client={productFileClient}
                                                      queryClient={getNamespacedQueryClient(
                                                        "productFile",
                                                      )}
                                                    >
                                                      <projectRouter.Provider
                                                        client={projectClient}
                                                        queryClient={getNamespacedQueryClient(
                                                          "project",
                                                        )}
                                                      >
                                                        <shopMetadataRouter.Provider
                                                          client={
                                                            shopMetadataClient
                                                          }
                                                          queryClient={getNamespacedQueryClient(
                                                            "shopMetadata",
                                                          )}
                                                        >
                                                          <stripeConnectRouter.Provider
                                                            client={
                                                              stripeConnectClient
                                                            }
                                                            queryClient={getNamespacedQueryClient(
                                                              "stripeConnect",
                                                            )}
                                                          >
                                                            <stylesRouter.Provider
                                                              client={
                                                                stylesClient
                                                              }
                                                              queryClient={getNamespacedQueryClient(
                                                                "styles",
                                                              )}
                                                            >
                                                              <starterProjectsRouter.Provider
                                                                client={
                                                                  starterProjectsClient
                                                                }
                                                                queryClient={getNamespacedQueryClient(
                                                                  "starterProjects",
                                                                )}
                                                              >
                                                                <subscriptionRouter.Provider
                                                                  client={
                                                                    subscriptionClient
                                                                  }
                                                                  queryClient={getNamespacedQueryClient(
                                                                    "subscription",
                                                                  )}
                                                                >
                                                                  <triggerRouter.Provider
                                                                    client={
                                                                      triggerClient
                                                                    }
                                                                    queryClient={getNamespacedQueryClient(
                                                                      "trigger",
                                                                    )}
                                                                  >
                                                                    <userRouter.Provider
                                                                      client={
                                                                        userClient
                                                                      }
                                                                      queryClient={getNamespacedQueryClient(
                                                                        "user",
                                                                      )}
                                                                    >
                                                                      <v2CustomDomainRouter.Provider
                                                                        client={
                                                                          v2CustomDomainClient
                                                                        }
                                                                        queryClient={getNamespacedQueryClient(
                                                                          "v2CustomDomain",
                                                                        )}
                                                                      >
                                                                        <v2ShopDeploymentRouter.Provider
                                                                          client={
                                                                            v2ShopDeploymentClient
                                                                          }
                                                                          queryClient={getNamespacedQueryClient(
                                                                            "v2ShopDeployment",
                                                                          )}
                                                                        >
                                                                          <workspaceRouter.Provider
                                                                            client={
                                                                              workspaceClient
                                                                            }
                                                                            queryClient={getNamespacedQueryClient(
                                                                              "workspace",
                                                                            )}
                                                                          >
                                                                            <activationRouter.Provider
                                                                              client={
                                                                                activationClient
                                                                              }
                                                                              queryClient={getNamespacedQueryClient(
                                                                                "activation",
                                                                              )}
                                                                            >
                                                                              <agentIntegrationsRouter.Provider
                                                                                client={
                                                                                  agentIntegrationsClient
                                                                                }
                                                                                queryClient={getNamespacedQueryClient(
                                                                                  "agentIntegrations",
                                                                                )}
                                                                              >
                                                                                <opencodeRouter.Provider
                                                                                  client={
                                                                                    opencodeClient
                                                                                  }
                                                                                  queryClient={getNamespacedQueryClient(
                                                                                    "opencode",
                                                                                  )}
                                                                                >
                                                                                  {
                                                                                    children
                                                                                  }
                                                                                </opencodeRouter.Provider>
                                                                              </agentIntegrationsRouter.Provider>
                                                                            </activationRouter.Provider>
                                                                          </workspaceRouter.Provider>
                                                                        </v2ShopDeploymentRouter.Provider>
                                                                      </v2CustomDomainRouter.Provider>
                                                                    </userRouter.Provider>
                                                                  </triggerRouter.Provider>
                                                                </subscriptionRouter.Provider>
                                                              </starterProjectsRouter.Provider>
                                                            </stylesRouter.Provider>
                                                          </stripeConnectRouter.Provider>
                                                        </shopMetadataRouter.Provider>
                                                      </projectRouter.Provider>
                                                    </productFileRouter.Provider>
                                                  </productRouter.Provider>
                                                </onboardingRouter.Provider>
                                              </nextJSProjectCacheRouter.Provider>
                                            </nextJSProjectRouter.Provider>
                                          </modifiersRouter.Provider>
                                        </marketingRouter.Provider>
                                      </integrationRouter.Provider>
                                    </funnelRouter.Provider>
                                  </fontRouter.Provider>
                                </fileVersionRouter.Provider>
                              </fileTemplateRouter.Provider>
                            </experimentRouter.Provider>
                          </entriRouter.Provider>
                        </elementRouter.Provider>
                      </daytonaRouter.Provider>
                    </chatRouter.Provider>
                  </billingRouter.Provider>
                </authRouter.Provider>
              </assetRouter.Provider>
            </announcementRouter.Provider>
          ),
        })}
      </analyticsLinkRouter.Provider>
    </analyticsRouter.Provider>
  );
}
